import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, Card, EmptyState, Spinner, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/formatters';

interface Row {
  id: string;
  status: string;
  event: { id: string; title: string; startTime: string; location: string | null; refundPolicy: string; price: number };
}

export function MyRegistrations() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery<{ upcoming: Row[]; past: Row[] }>({
    queryKey: ['my-regs'],
    queryFn: async () => (await api.get('/public/me/registrations')).data,
  });

  const cancel = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/public/me/registrations/${id}/cancel`);
    },
    onSuccess: () => {
      toast.push('Registration cancelled', 'success');
      void qc.invalidateQueries({ queryKey: ['my-regs'] });
    },
    onError: (err) => toast.push(errorMessage(err), 'error'),
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl">My registrations</h1>

      <section className="space-y-2">
        <h2 className="font-display text-xl">Upcoming</h2>
        {(data?.upcoming ?? []).length === 0 ? (
          <EmptyState title="Nothing coming up" />
        ) : (
          <ul className="space-y-3">
            {data!.upcoming.map((r) => (
              <li key={r.id}>
                <Card className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{r.event.title}</div>
                    <div className="text-sm text-ink-muted">{formatDateTime(r.event.startTime)}</div>
                  </div>
                  <Button variant="ghost" onClick={() => cancel.mutate(r.id)}>
                    Cancel
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="font-display text-xl">Past</h2>
        {(data?.past ?? []).length === 0 ? (
          <EmptyState title="No past registrations" />
        ) : (
          <ul className="space-y-3">
            {data!.past.map((r) => (
              <li key={r.id}>
                <Card className="p-4">
                  <div className="font-medium">{r.event.title}</div>
                  <div className="text-sm text-ink-muted">{formatDateTime(r.event.startTime)}</div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
